import React, { useState, useCallback } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import styles from "./crop.module.css";
import { gql, useMutation } from "@apollo/client";
import getCroppedImg from "../../lib/cropImage";
import { Graph } from "src/generated/graph";
import { Modal } from "@shopify/polaris";

const UPLOAD = gql`
  mutation upload($file: Upload!) {
    upload(file: $file) {
      filename
      url
    }
  }
`;

interface Props {
  value: any;
  onSuccess: (image: { src: string; color: string; hash?: string }) => void;
  onClose: () => void;
  only?: "square" | "portrait";
  polaris?: boolean;
}

export function ProductDraftCropImage(props: Props) {
  const [change, setChange] = useState(false);
  const [crop, setCrop] = useState<any>({});
  const [quality] = useState(0.5);
  const [upload_mutation, { loading }] = useMutation<Graph.Mutation>(UPLOAD, {
    onCompleted: (r) => {
      props.onSuccess({ src: r.upload.url, color: "", hash: props.value.hash });
    },
  });
  const [aspect, setAspect] = useState<"square" | "portrait">("square");

  const onCropImg = useCallback(
    (e) => {
      const file = new File([e], "newFile.png");

      // if (Math.round(file.size / 1000) > 400) {
      //   alert("image after resize and crop but size are still more than 300KB!");
      //   return;
      // }

      upload_mutation({
        variables: {
          file: file,
        },
      }).catch((e) => {
        console.log(e);
      });
    },
    [upload_mutation],
  );

  const onCrop = (event: CustomEvent<any>) => {
    setCrop(event);
  };

  const handleClickCrop = async () => {
    await getCroppedImg(crop.target.src, crop.detail, 0, quality, onCropImg);
  };

  if (props.polaris) {
    return (
      <div className={`${styles.bg_overlay}`}>
        <div>
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={styles.title}>Cropping</div>
              {!loading && (
                <div className={styles.close}>
                  <i className="fas fa-times-circle" onClick={() => props.onClose()} />
                </div>
              )}
            </div>
            <div className={styles.content}>
              {change ? (
                <div></div>
              ) : (
                <Cropper
                  style={{
                    height: 422,
                    width: "100%",
                  }}
                  src={props.value.src + ""}
                  aspectRatio={aspect === "square" ? 1 / 1 : 7.5 / 10.5}
                  className="cropper"
                  allowFullScreen={true}
                  allowTransparency={false}
                  autoCropArea={1}
                  crop={onCrop}
                  viewMode={2}
                  guides={false}
                />
              )}
            </div>
            <div className={styles.menu}>
              <div style={{ flexGrow: 1 }}>
                {!loading && (
                  <ul className={styles.option}>
                    {props.only ? (
                      <>
                        {props.only === "square" ? (
                          <li
                            className={aspect === "square" ? styles.option_item_active : ""}
                            onClick={() => {
                              setChange(true);
                              setAspect("square");
                              setTimeout(() => {
                                setChange(false);
                              }, 300);
                            }}
                          >
                            <i className="fas fa-square" />
                            Square
                          </li>
                        ) : (
                          <li
                            className={aspect === "portrait" ? styles.option_item_active : ""}
                            onClick={() => {
                              setChange(true);
                              setAspect("portrait");
                              setTimeout(() => {
                                setChange(false);
                              }, 300);
                            }}
                          >
                            <i className="fas fa-rectangle-portrait" />
                            Portrait
                          </li>
                        )}
                      </>
                    ) : (
                      <>
                        <li
                          className={aspect === "square" ? styles.option_item_active : ""}
                          onClick={() => {
                            setChange(true);
                            setAspect("square");
                            setTimeout(() => {
                              setChange(false);
                            }, 300);
                          }}
                        >
                          <i className="fas fa-square" />
                          Square
                        </li>
                        <li
                          className={aspect === "portrait" ? styles.option_item_active : ""}
                          onClick={() => {
                            setChange(true);
                            setAspect("portrait");
                            setTimeout(() => {
                              setChange(false);
                            }, 300);
                          }}
                        >
                          <i className="fas fa-rectangle-portrait" />
                          Portrait
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </div>
              <div style={{ paddingRight: 10 }}>
                {!loading ? (
                  <button className="btn btn-primary btn-sm" onClick={() => handleClickCrop()} type="button">
                    Crop{" "}
                  </button>
                ) : (
                  <button className="btn btn-primary" type="button" disabled>
                    <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                    Uploading...
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={true}
      onClose={props.onClose}
      title="Cropping"
      loading={loading}
      primaryAction={{
        content: loading ? "Uploading..." : "Crop",
        onAction: handleClickCrop,
        disabled: loading,
        loading: loading,
      }}
      secondaryActions={
        props.only
          ? [
              {
                content: props.only,
                onAction: () => {
                  setChange(true);
                  if (props.only === "square") {
                    setAspect(props.only);
                  }
                  setTimeout(() => {
                    setChange(false);
                  }, 300);
                },
                icon:
                  props.only === "square"
                    ? ((<i className="fas fa-square" />) as any)
                    : ((<i className="fas fa-rectangle-portrait" />) as any),
              },
            ]
          : [
              {
                content: "Square",
                onAction: () => {
                  setChange(true);
                  setAspect("square");
                  setTimeout(() => {
                    setChange(false);
                  }, 300);
                },
                icon: (<i className="fas fa-square" />) as any,
              },
              {
                content: "Portrait",
                onAction: () => {
                  setChange(true);
                  setAspect("portrait");
                  setTimeout(() => {
                    setChange(false);
                  }, 300);
                },
                icon: (<i className="fas fa-rectangle-portrait" />) as any,
              },
            ]
      }
    >
      <Modal.Section flush>
        <div style={{ height: 422, position: "relative", overflow: "auto" }}>
          {change ? (
            <div></div>
          ) : (
            <Cropper
              style={{
                height: 422,
                width: "100%",
              }}
              src={props.value.src + ""}
              aspectRatio={aspect === "square" ? 1 / 1 : 7.5 / 10.5}
              className="cropper"
              allowFullScreen={true}
              allowTransparency={false}
              autoCropArea={1}
              crop={onCrop}
              viewMode={2}
              guides={false}
            />
          )}
        </div>
      </Modal.Section>
    </Modal>
  );

  /*
  return (
    <div className={styles.bg_overlay}>
      <div>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>Cropping</div>
            {!loading && (
              <div className={styles.close}>
                <i className="fas fa-times-circle" onClick={() => props.onClose()} />
              </div>
            )}
          </div>
          <div className={styles.content}>
            {change ? (
              <div></div>
            ) : (
              <Cropper
                style={{
                  height: 422,
                  width: "100%",
                }}
                src={props.value.src + ""}
                aspectRatio={aspect === "square" ? 1 / 1 : 7.5 / 10.5}
                className="cropper"
                allowFullScreen={true}
                allowTransparency={false}
                autoCropArea={1}
                crop={onCrop}
                viewMode={2}
                guides={false}
              />
            )}
          </div>
          <div className={styles.menu}>
            <div style={{ flexGrow: 1 }}>
              {!loading && (
                <ul className={styles.option}>
                  {props.only ? (
                    <>
                      {props.only === "square" ? (
                        <li
                          className={aspect === "square" ? styles.option_item_active : ""}
                          onClick={() => {
                            setChange(true);
                            setAspect("square");
                            setTimeout(() => {
                              setChange(false);
                            }, 300);
                          }}
                        >
                          <i className="fas fa-square" />
                          Square
                        </li>
                      ) : (
                        <li
                          className={aspect === "portrait" ? styles.option_item_active : ""}
                          onClick={() => {
                            setChange(true);
                            setAspect("portrait");
                            setTimeout(() => {
                              setChange(false);
                            }, 300);
                          }}
                        >
                          <i className="fas fa-rectangle-portrait" />
                          Portrait
                        </li>
                      )}
                    </>
                  ) : (
                    <>
                      <li
                        className={aspect === "square" ? styles.option_item_active : ""}
                        onClick={() => {
                          setChange(true);
                          setAspect("square");
                          setTimeout(() => {
                            setChange(false);
                          }, 300);
                        }}
                      >
                        <i className="fas fa-square" />
                        Square
                      </li>
                      <li
                        className={aspect === "portrait" ? styles.option_item_active : ""}
                        onClick={() => {
                          setChange(true);
                          setAspect("portrait");
                          setTimeout(() => {
                            setChange(false);
                          }, 300);
                        }}
                      >
                        <i className="fas fa-rectangle-portrait" />
                        Portrait
                      </li>
                    </>
                  )}
                </ul>
              )}
            </div>

            <div style={{ paddingRight: 10 }}>
              {!loading ? (
                <button className="btn btn-primary btn-sm" onClick={() => handleClickCrop()} type="button">
                  Crop{" "}
                </button>
              ) : (
                <button className="btn btn-primary" type="button" disabled>
                  <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                  Uploading...
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  */
}
